import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { Article } from '../../../types/article';
import { timeAgo } from '../../../utils/timeAgo';

const categoryTag: Record<string, string> = {
  Warzone: 'WZ',
  Multiplayer: 'MP',
  Zombies: 'ZM',
  Esports: 'CDL',
  Community: 'COM',
  Leaks: 'LEAK',
};

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

function SidebarCard({ article }: { article: Article }) {
  const slug = article.slug || article.id;
  return (
    <Link to={`/news/${slug}`} className="group block border-b border-[#1e1e1e] pb-6 last:border-0 last:pb-0 cursor-pointer">
      <div className="w-full h-36 overflow-hidden mb-3">
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-[#1a1a1a]" />
        )}
      </div>
      <div className="flex items-center justify-between mb-2">
        <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
        <span className="text-[#666] text-xs font-['Barlow_Condensed'] font-bold tracking-wider">
          {timeAgo(article.date)}
        </span>
      </div>
      <h3 className="font-['Barlow_Condensed'] font-black text-white text-base leading-tight uppercase group-hover:text-[#00ff41] transition-colors line-clamp-3">
        {article.title}
      </h3>
    </Link>
  );
}

export default function HeroSection() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      const { data } = await supabase
        .from('articles')
        .select('*')
        .eq('published', true)
        .order('date', { ascending: false })
        .limit(4);
      setArticles((data as Article[]) ?? []);
      setLoading(false);
    };
    fetch_();
  }, []);

  const featured = articles[0] ?? null;
  const sidebar = articles.slice(1, 3);

  if (loading) {
    return (
      <div className="pt-14 bg-[#111] min-h-[500px] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-[#00ff41] text-3xl" />
      </div>
    );
  }

  if (!featured) {
    return (
      <div className="pt-14 bg-[#111] min-h-[400px] flex flex-col items-center justify-center text-center px-6 py-20">
        <i className="ri-article-line text-5xl text-[#333] mb-4" />
        <h2 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase mb-2">No Articles Yet</h2>
        <p className="text-[#666] text-sm mb-6">Head to the admin panel to publish your first article.</p>
        <a
          href="/admin"
          className="font-['Barlow_Condensed'] font-black text-sm tracking-widest bg-[#00ff41] text-black px-6 py-3 cursor-pointer whitespace-nowrap"
        >
          GO TO ADMIN
        </a>
      </div>
    );
  }

  return (
    <section className="pt-14 bg-[#111]">
      {/* Dot texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #2a2a2a 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          opacity: 0.35,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-8">

          {/* ── LEFT: Featured Article ─────────────────────── */}
          <div className="lg:col-span-2 border-r border-[#1e1e1e] pr-0 lg:pr-8">
            <Link to={`/news/${featured.slug || featured.id}`} className="group block cursor-pointer">
              {/* Image */}
              <div className="relative w-full overflow-hidden" style={{ height: '360px' }}>
                {featured.image ? (
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
                    <i className="ri-image-line text-[#333] text-5xl" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#111]/60 to-transparent" />
              </div>

              {/* Meta */}
              <div className="py-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Tag label={categoryTag[featured.category] ?? featured.category.toUpperCase()} />
                    <Tag label="NEWS" />
                  </div>
                  <span className="text-[#666] text-xs font-['Barlow_Condensed'] font-bold tracking-wider">
                    {timeAgo(featured.date)}
                  </span>
                </div>

                <h1 className="font-['Barlow_Condensed'] font-black text-white text-4xl leading-tight uppercase mb-3 group-hover:text-[#00ff41] transition-colors">
                  {featured.title}
                </h1>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Tag label="NEWS" />
                  <Tag label={featured.category.toUpperCase()} />
                </div>

                <p className="text-[#888] text-sm leading-relaxed line-clamp-3">
                  {featured.excerpt}
                </p>
              </div>
            </Link>
          </div>

          {/* ── RIGHT: Sidebar ──────────────────────────────── */}
          <div className="lg:col-span-1 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#1e1e1e]">
            <div className="space-y-6">
              {sidebar.length > 0 ? (
                sidebar.map((a) => <SidebarCard key={a.id} article={a} />)
              ) : (
                <p className="text-[#555] text-sm font-['Barlow_Condensed']">More articles coming soon.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
